/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useMutation } from 'react-query';
import { AxiosError, AxiosResponse } from 'axios';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import { useNavigate, useParams } from 'react-router-dom';
import {
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';

import isLength from 'validator/lib/isLength';


import { QueryParams } from '@/src/types/general.types';
import { Locker } from '@/src/types/locker.types';
import {CourierCode} from '../../types/customer.types';
import { useLockers } from '../../services/requests';

import {
  MIN_COURIER_CODE_LENGTH,
  MAX_COURIER_CODE_LENGTH
} from '../../constants';

import { getErrorMessage } from '../../utils';
import LoadingComponent from '../common/LoadingComponent';
import ErrorDialog from '../common/ErrorDialog';
import FormFieldInput from '../common/FormFieldInput';


export interface CourierCodeFormProps {
  mutationFn: (data: CourierCode) => Promise<AxiosResponse>;
  courierCode: CourierCode;
  pageTitle?: string;
  state?: any;
}

export default function CourierCodeForm({
  mutationFn,
  courierCode,
  pageTitle,
  state
}: CourierCodeFormProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const courierMutation = useMutation((data: CourierCode) => mutationFn(data), {
    onSuccess: () => navigate(`/customers/${id}/courier-codes`, { state: { companyName: state.companyName } })
  });
  const [courierCodeData, setCourierCodeData] = useState(courierCode);
  const [errors, setErrors] = useState({
    courierCodeError: '',
    lockerIdError: '',
  });

  const { t, i18n } = useTranslation();

  const {
    data: lockersData,
    isLoading: loadingLockers,
    error: lockersError,
    isError: isLockerError,
  } = useLockers({ page: 1, pageSize: 5000 } as unknown as QueryParams);


  useEffect(() => {
    if (courierCode) {
      setCourierCodeData({ ...courierCode });
    }
  }, [courierCode]);

  useEffect(() => {
    setErrors({
      courierCodeError: '',
      lockerIdError: '',
    });
  }, [i18n.language]);



  const validateCourierCodeData = (data: CourierCode): boolean => {
    const isCourierCodeValid = isLength(
      data.courierCode as string,
      {
        min: MIN_COURIER_CODE_LENGTH,
        max: MAX_COURIER_CODE_LENGTH,
      },
    );

    const isCustomerIdValid = isLength(data.customerId as string, {min: 1})

    return (
      isCourierCodeValid && isCustomerIdValid
    );
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (validateCourierCodeData(courierCodeData)) {

      courierMutation.mutate({
        ...courierCodeData,
      });
    }
  };

  const handleOnChangeFields = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string | number | null>
      
  ) => {

    setCourierCodeData({
      ...courierCodeData,
      [e.target.name]: e.target.value,
    });
    
    setErrors({
      ...errors,
      [`${e.target.name}Error`]: '',
    });
  };

  const getError = () => {
    let currentError = '';
    if (isLockerError) {
      currentError = getErrorMessage(
        lockersError as AxiosError,
        t('lockers_err')
      );
    }
    const errorList = {
      editErrorMsg: t('save_data_err'),
      createErrorMsg: t('save_data_err'),
    };
    if (courierMutation.isError) {
      const { error: createUserError } = courierMutation;

      currentError = getErrorMessage(
        createUserError as AxiosError,
        errorList.createErrorMsg
      );
    }
    if (currentError) {
      return <ErrorDialog status message={currentError} />;
    }
    return null;
  };

  return (
    <Box>
      {(courierMutation.isLoading || loadingLockers) && (
        <LoadingComponent />
      )}
      {getError()}
      {lockersData && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <form onSubmit={handleSubmit}>
            {pageTitle && (
              <Typography
                variant='h4'
                sx={{ mb: 3, display: 'flex', justifyContent: 'flex-start' }}
              >
                {t(pageTitle)}
              </Typography>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1 }}
              sx={{ mb: 2 }}
              alignItems='baseline'
            >
              <InputLabel id='locker_id' sx={{ mb: 1, width: 200 }}>
                {`${t('locker_id')}:`}
                <Typography sx={{ display: 'inline', color: 'red' }}>*</Typography>
              </InputLabel>
              <Select
                displayEmpty
                name='lockerId'
                value={courierCodeData.lockerId ?? ''}
                onChange={(e) => handleOnChangeFields(e)}
                sx={{ width: 320 }}
                labelId='locker_id'
              >
                <MenuItem key='all_lockers' value=""><em>{t('all_lockers')}</em></MenuItem>
                {lockersData.data.map((locker:Locker) => (
                  <MenuItem key={locker.lockerId} value={locker.lockerId}>
                    {locker.lockerCode} - {locker.name}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <FormFieldInput
              label={`${t('courier_code')}:`}
              isRequired
              name='courierCode'
              htmlFor='courierCode'
              value={courierCodeData.courierCode}
              hasValidation={!!errors.courierCodeError}
              errorMsg={errors.courierCodeError}
              onChange={(e) => handleOnChangeFields(e)}
              labelWidth={200}
            />

            <Stack
              direction='row'
              spacing={{ xs: 1 }}
              sx={{ mb: 2 }}
              alignItems='flex-start'
            >
              <Typography sx={{ display: 'inline', color: 'red' }}>*</Typography>
              {t('required_field')}
            </Stack>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button type='submit' variant='contained' sx={{ textTransform: 'capitalize' }} size='medium'>
                {t('save')}
              </Button>
            </Box>
          </form>
        </Box>
      )}
    </Box>
  );
}

